import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

const storageValueFactory = <T>(
  key: string,
  storage = () => window.sessionStorage,
) => {
  const dispatchEvent = (newValue: string | null) => {
    window.dispatchEvent(
      new StorageEvent("storage", { key, newValue, storageArea: storage() }),
    );
  };

  return {
    set: (value: T) => {
      const stringifiedValue = JSON.stringify(value);
      storage().setItem(key, stringifiedValue);
      dispatchEvent(stringifiedValue);
    },
    get: () => storage().getItem(key),
    remove: () => {
      storage().removeItem(key);
      dispatchEvent(null);
    },
    subscribe: (callback: (ev: StorageEvent) => void) => {
      window.addEventListener("storage", callback);
      return () => window.removeEventListener("storage", callback);
    },
  };
};

export const useSessionStorage = <T>(
  key: string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] => {
  const sessionStorageValue = useMemo(() => storageValueFactory(key), [key]);

  const store = useSyncExternalStore(
    sessionStorageValue.subscribe,
    sessionStorageValue.get,
    () => JSON.stringify(initialValue),
  );

  const setState = useCallback(
    (v: T | SetStateAction<T>) => {
      try {
        let nextState: T;
        if (typeof v === "function") {
          const oldState = sessionStorageValue.get();
          nextState = (v as (prev: T) => T)(
            typeof oldState === "string" ? JSON.parse(oldState) : oldState,
          );
        } else {
          nextState = v;
        }

        if (nextState === undefined || nextState === null) {
          sessionStorageValue.remove();
        } else {
          sessionStorageValue.set(nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [sessionStorageValue],
  );

  useEffect(() => {
    if (!!initialValue && sessionStorageValue.get() === null) {
      sessionStorageValue.set(initialValue);
    }
  }, [sessionStorageValue, initialValue]);

  return [store ? JSON.parse(store) : initialValue, setState];
};
